import {
  Box,
  Paper,
  Typography
} from "@mui/material";
import { calculateAge } from '../../utils/age';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  FitnessCenter as FitnessCenterIcon,
  EmojiEvents as GoalsIcon,
  Schedule as ScheduleIcon,
  Update as LastTrainedIcon,
  Security as StatusIcon,
  Build as EquipmentIcon,
  Accessibility as BodyPartIcon,
  Height as HeightIcon
} from "@mui/icons-material";
import moment from "moment";
import {experienceLevels} from "../../enums/ExperienceLevels";
import {EquipmentTypes} from "../../enums/EquipmentType";
import Loader from "../Loader";
import {TemplateGoals} from "../../enums/TemplateGoals";
import {bodyParts} from "../../enums/BodyParts";
import {formatPhoneForDisplay} from "../shared/PhoneInput";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {StatusChip} from "../ui";
import {RTL} from "../../cache/CacheRtl";

const ClientProfileSection = ({client, coach = false}) => {
  if (!client) {
    return <Loader loading={true} />;
  }

  const getBodyPartName = (value) => {
    const found = bodyParts.find(bp => bp.value === value);
    return found ? found.name : "אין מידע";
  };

  const displayAge = (dateOfBirth) => {
    return calculateAge(dateOfBirth) ?? "אין מידע";
  };

  const getGenderText = (gender) => {
    switch (gender) {
      case "FEMALE":
        return "נקבה";
      case "MALE":
        return "זכר";
      default:
        return "אין מידע";
    }
  };

  const Row = (props) => <ProfileRow {...props} coach={coach}/>;

  return (
      <RTL>
        <Paper
            dir="rtl"
            sx={{
              backgroundColor: coach ? coachT.white : colors.white,
              border: coach ? `1px solid ${coachT.border}` : borders.default,
              borderRadius: coach ? coachT.r : borders.radius.lg,
              boxShadow: coach ? coachT.sh : shadows.card,
              overflow: "hidden",
            }}
            elevation={0}
        >
          {/* Header */}
          <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: coach ? "16px 22px" : "16px 20px",
                backgroundColor: coach ? coachT.bg : colors.bg,
                borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
              }}
          >
            <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
              {coach ? (
                  <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <PersonIcon sx={{color: coachT.t2, fontSize: 22}}/>
                  </Box>
              ) : (
                  <PersonIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
              )}
              <Typography sx={{fontWeight: coach ? 800 : 600, fontSize: coach ? "17px" : "16px", color: coach ? coachT.text : colors.textPrimary, letterSpacing: coach ? "-0.2px" : 0}}>
                פרטים אישיים
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{padding: coach ? "20px 22px" : "20px"}}>
            <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)"
                  },
                  gap: "16px 24px",
                  rowGap: "12px"
                }}
            >
              <Row
                  icon={<PersonIcon />}
                  label="שם מלא"
                  value={client.name}
              />

              <Row
                  icon={<CakeIcon />}
                  label="גיל"
                  value={displayAge(client.dateOfBirth)}
              />

              <Row
                  icon={<GenderIcon />}
                  label="מגדר"
                  value={getGenderText(client.gender)}
              />

              <Row
                  icon={<HeightIcon />}
                  label="גובה"
                  value={client.height ? `${client.height} ס״מ` : "אין מידע"}
              />

              <Row
                  icon={<StatusIcon />}
                  label="סטטוס"
                  value={
                    <StatusChip
                        variant={client.enabled ? "success" : "error"}
                        label={client.enabled ? "פעיל" : "חסום"}
                    />
                  }
              />

              <Row
                  icon={<EmailIcon />}
                  label="מייל"
                  value={client.email || "אין מידע"}
              />

              <Row
                  icon={<PhoneIcon />}
                  label="טלפון"
                  value={
                    <Typography sx={{ direction: "ltr", fontSize: "14px", fontWeight: 600, color: colors.textPrimary }}>
                      {formatPhoneForDisplay(client.phoneNumber) || "אין מידע"}
                    </Typography>
                  }
              />

              <Row
                  icon={<ScheduleIcon />}
                  label="אימוניים בשבוע"
                  value={client.numOfDaysTraining || "אין מידע"}
              />

              <Row
                  icon={<FitnessCenterIcon />}
                  label="רמת מתאמן"
                  value={client.experienceLevel ? experienceLevels.find(lvl => lvl.value === client.experienceLevel)?.name : "אין מידע"}
              />

              <Row
                  icon={<EquipmentIcon />}
                  label="ציוד קיים"
                  value={client.equipmentType ? EquipmentTypes.find(eq => eq.value === client.equipmentType)?.name : "אין מידע"}
              />

              <Row
                  icon={<LastTrainedIcon />}
                  label="אימון אחרון"
                  value={client.lastTrained ? moment.unix(client.lastTrained).format("DD/MM/YY HH:mm") : "אין מידע"}
              />

              <Row
                  icon={<CalendarIcon />}
                  label="תאריך יצירה"
                  value={moment.unix(client.createdDate).format("DD/MM/YY")}
              />

              <Row
                  icon={<CalendarIcon />}
                  label="תוקף מנוי"
                  value={client.expirationDate ? moment.unix(client.expirationDate).format("DD/MM/YY") : "אין מידע"}
              />

              <Row
                  icon={<BodyPartIcon />}
                  label="אזור מטרה עיקרי"
                  value={getBodyPartName(client.bodyPart)}
              />

              <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}>
                <Row
                    icon={<GoalsIcon />}
                    label="מטרות"
                    value={client.goals?.length ? client.goals.map(goal => TemplateGoals[goal] || goal).join(", ") : "אין מידע"}
                />
              </Box>

                <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}>
                    <Row
                        icon={<BodyPartIcon />}
                        label="אזורי מטרה נוספים"
                        value={
                            client.bodyPartSpecific?.length
                                ? client.bodyPartSpecific
                                    .map(value => {
                                        const muscle = bodyParts.find(m => m.value === value);
                                        return muscle ? muscle.name : value;
                                    })
                                    .join(", ")
                                : "אין מידע"
                        }
                    />
                </Box>
            </Box>
          </Box>
        </Paper>
      </RTL>
  );
};

const ProfileRow = ({icon, label, value, coach = false}) => {
  if (coach) {
    return (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          padding: "8px 0", borderBottom: `1px solid ${coachT.borderLight}`, minHeight: 36,
        }}>
          <Box sx={{color: coachT.t3, display: "flex", alignItems: "center", flexShrink: 0, "& .MuiSvgIcon-root": {fontSize: "16px"}}}>
            {icon}
          </Box>
          <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: coachT.t2, flexShrink: 0}}>
            {label}
          </Typography>
          <Box sx={{flex: 1}}/>
          {typeof value === 'string' ? (
              <Typography sx={{fontSize: "13px", fontWeight: 700, color: coachT.text, textAlign: "left", wordBreak: "break-word"}}>
                {value}
              </Typography>
          ) : (
              value
          )}
        </Box>
    );
  }
  return (
      <Box sx={{minHeight: "24px"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 0.5, marginBottom: "2px"}}>
          <Box
              sx={{
                color: colors.textSecondary,
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                flexShrink: 0,
                "& .MuiSvgIcon-root": {fontSize: "16px"}
              }}
          >
            {icon}
          </Box>
          <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: colors.textSecondary
              }}
          >
            {label}:
          </Typography>
        </Box>
        <Box>
          {typeof value === 'string' ? (
              <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: colors.textPrimary,
                    lineHeight: "18px",
                    wordBreak: "break-word"
                  }}
              >
                {value}
              </Typography>
          ) : (
              value
          )}
        </Box>
      </Box>
  );
};

export default ClientProfileSection